import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import Sidebar from '../components/Sidebar'

interface CVRecord {
  id: string
  job_title_target: string | null
  company_target: string | null
  approved_at: string | null
  docx_available: boolean
  pdf_available: boolean
  iteration_count: number
}

type DownloadKind = 'docx' | 'pdf'

interface HistoryResponse {
  data: {
    cv_records: CVRecord[]
  }
}

export default function CVHistoryPage() {
  const navigate = useNavigate()
  const [records, setRecords] = useState<CVRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<HistoryResponse>('/api/v1/cv/history')
      setRecords(res.data.cv_records)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  function isStillGenerating(r: CVRecord): boolean {
    if (r.docx_available || !r.approved_at) return false
    return Date.now() - new Date(r.approved_at).getTime() < 2 * 60 * 1000
  }

  // Poll every 3s only while a CV approved within the last 2 minutes lacks its DOCX.
  // After 2 minutes without docx_available, treat the export as failed and stop polling.
  useEffect(() => {
    const hasPending = records.some(isStillGenerating)
    if (hasPending) {
      pollRef.current = setInterval(fetchHistory, 3000)
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [records, fetchHistory])

  const handleDownload = async (id: string, kind: DownloadKind) => {
    setDownloadingKey(`${id}:${kind}`)
    try {
      const res = await api.get<{ data: { download_url: string; filename: string } }>(
        `/api/v1/cv/history/${id}/download/${kind}`,
      )
      const { download_url, filename } = res.data
      const blob = await fetch(download_url).then((r) => r.blob())
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setDownloadingKey(null)
    }
  }

  const handleGeneratePdf = async (id: string) => {
    setGeneratingPdfId(id)
    setError(null)
    try {
      await api.post(`/api/v1/cv/history/${id}/export/retry-pdf`, {})
      await fetchHistory()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setGeneratingPdfId(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this CV permanently? This cannot be undone.')) return
    setDeletingId(id)
    try {
      await api.delete(`/api/v1/cv/history/${id}`)
      setRecords((prev) => prev.filter((r) => r.id !== id))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setDeletingId(null)
    }
  }

  function displayName(r: CVRecord): string {
    const parts: string[] = []
    if (r.job_title_target) parts.push(r.job_title_target)
    if (r.company_target) parts.push(r.company_target)
    return parts.length > 0 ? parts.join(' — ') : 'Untitled CV'
  }

  function formatDate(iso: string | null): string {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="min-h-screen bg-[#faf9f6] flex">
      <Sidebar />

      <main className="flex-1 ml-16 lg:ml-64 max-w-3xl px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-['IBM_Plex_Serif'] font-semibold text-[#1a1c1a] tracking-tight">
              CV History
            </h1>
            <p className="text-xs text-[#877369] mt-1">
              All your approved CVs in one place
            </p>
          </div>
          <button
            onClick={() => navigate('/cv/generate')}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] transition-all shadow-sm"
          >
            Generate New CV
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <span className="animate-spin inline-block w-5 h-5 border-2 border-[#833c0b]/30 border-t-[#833c0b] rounded-full" />
          </div>
        )}

        {/* Empty state */}
        {!loading && records.length === 0 && (
          <div className="text-center py-20 space-y-4">
            <span className="material-symbols-outlined text-5xl text-[#dac2b6]">
              description
            </span>
            <p className="text-sm text-[#877369]">
              No CVs yet. Generate your first tailored CV.
            </p>
            <button
              onClick={() => navigate('/cv/generate')}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-[#622800] to-[#833c0b] hover:from-[#833c0b] hover:to-[#622800] transition-all"
            >
              Generate CV
            </button>
          </div>
        )}

        {/* CV list */}
        {!loading && records.length > 0 && (
          <div className="space-y-3">
            {records.map((r) => (
              <div
                key={r.id}
                className="bg-white rounded-xl border border-[#dac2b6]/20 px-5 py-4 flex items-center justify-between hover:shadow-sm transition-shadow"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1a1c1a] truncate">
                    {displayName(r)}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-[#877369]">
                      {formatDate(r.approved_at)}
                    </span>
                    {r.iteration_count > 0 && (
                      <span className="text-[10px] text-[#877369] bg-[#f4f3f0] px-2 py-0.5 rounded">
                        {r.iteration_count} edit{r.iteration_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {r.docx_available ? (
                    <>
                      <button
                        onClick={() => handleDownload(r.id, 'docx')}
                        disabled={downloadingKey !== null}
                        className="p-2 text-[#545f6e] hover:text-[#833c0b] hover:bg-[#f4f3f0] rounded-lg transition-colors disabled:opacity-50"
                        title="Download DOCX"
                      >
                        <span className="material-symbols-outlined text-lg">
                          {downloadingKey === `${r.id}:docx` ? 'hourglass_empty' : 'description'}
                        </span>
                      </button>
                      {r.pdf_available ? (
                        <button
                          onClick={() => handleDownload(r.id, 'pdf')}
                          disabled={downloadingKey !== null}
                          className="p-2 text-[#545f6e] hover:text-[#833c0b] hover:bg-[#f4f3f0] rounded-lg transition-colors disabled:opacity-50"
                          title="Download PDF"
                        >
                          <span className="material-symbols-outlined text-lg">
                            {downloadingKey === `${r.id}:pdf` ? 'hourglass_empty' : 'picture_as_pdf'}
                          </span>
                        </button>
                      ) : (
                        r.approved_at && (
                          <button
                            onClick={() => handleGeneratePdf(r.id)}
                            disabled={generatingPdfId !== null}
                            className="p-2 text-[#545f6e] hover:text-[#833c0b] hover:bg-[#f4f3f0] rounded-lg transition-colors disabled:opacity-50"
                            title="Generate PDF"
                          >
                            <span className="material-symbols-outlined text-lg">
                              {generatingPdfId === r.id ? 'hourglass_empty' : 'picture_as_pdf'}
                            </span>
                          </button>
                        )
                      )}
                    </>
                  ) : isStillGenerating(r) ? (
                    <span className="text-xs text-[#877369] px-2" title="DOCX is still being generated">
                      Generating...
                    </span>
                  ) : (
                    <span className="text-xs text-red-400 px-2" title="DOCX export failed">
                      Export failed
                    </span>
                  )}
                  <button
                    onClick={() => handleDelete(r.id)}
                    disabled={deletingId === r.id}
                    className="p-2 text-[#877369] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete CV"
                  >
                    <span className="material-symbols-outlined text-lg">
                      {deletingId === r.id ? 'hourglass_empty' : 'delete'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
